import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { AIAgentType } from "../../entities/company/AIBilling/AIBilling";
import CompanyService from "../../services/CompanyService";
import TabPosition from "../shared/TabPosition";
import AgendaDrawer from "./components/AgendaDrawer";
import AgendaFullList from "./components/AgendaFullList";
import AgendaGate from "./components/AgendaGate";
import AgendaUnavailable from "./components/AgendaUnavailable";
import { useAgenda } from "./hooks/useAgenda";
import {
  AgendaTab,
  countByTab,
  filterAgendaItems,
  paginate
} from "./utils/agendaUtils";
import "./agenda.css";

/** Full agenda: tabs, client search and frontend pagination (10 per page). */
const AgendaFullContent = () => {
  const state = useAgenda();
  const { agenda, items } = state;
  const [tab, setTab] = useState<AgendaTab>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const counts = useMemo(() => countByTab(items), [items]);
  const filtered = useMemo(
    () => filterAgendaItems(items, tab, search),
    [items, tab, search]
  );
  const { pageItems, pageCount, page: currentPage } = paginate(filtered, page);

  return (
    <TabPosition module="company">
      <Helmet>
        <title>Intiza</title>
      </Helmet>
      <div className="agenda-page">
        <AgendaGate state={state}>
          {agenda && (
            <AgendaFullList
              agenda={agenda}
              tab={tab}
              counts={counts}
              pageItems={pageItems}
              filteredCount={filtered.length}
              page={currentPage}
              pageCount={pageCount}
              canEdit={state.canEdit}
              onTabChange={(next) => {
                setTab(next);
                setPage(0);
              }}
              onSearch={(value) => {
                setSearch(value);
                setPage(0);
              }}
              onPageChange={setPage}
              onRegenerate={state.requestRegenerate}
              onToggleCompleted={state.requestToggleCompleted}
              onOpen={state.selectItem}
            />
          )}
        </AgendaGate>
        <AgendaDrawer
          item={state.selectedItem}
          canEdit={state.canEdit}
          onClose={state.clearSelection}
          onPrimaryAction={state.runPrimaryAction}
          onToggleCompleted={state.requestToggleCompleted}
          onDelete={state.requestDelete}
        />
      </div>
    </TabPosition>
  );
};

/** Entry point: the agenda API is only queried when the agent is enabled. */
const AgendaFull = () =>
  CompanyService.canShowAgentAI(AIAgentType.Agenda) ? (
    <AgendaFullContent />
  ) : (
    <TabPosition module="company">
      <div className="agenda-page">
        <div className="agenda-card">
          <AgendaUnavailable />
        </div>
      </div>
    </TabPosition>
  );

export default AgendaFull;
